import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FormatType, NoteFormatResponse } from '../types';
import {
  fetchFormatTypes,
  generateNoteFormat,
  fetchNoteFormats,
  deleteNoteFormat,
} from '../services/noteFormatService';
import useAlertStore from '@/stores/alertStore';
import useNoteFormatDisplayStore from '@/stores/noteFormatDisplayStore';
import { errorReporter } from '@/services/errorReportingService';

interface UseNoteFormatsReturn {
  formatTypes: FormatType[];
  isLoadingTypes: boolean;
  generatedFormats: NoteFormatResponse[];
  isLoadingFormats: boolean;
  activeResult: NoteFormatResponse | null;
  isGenerating: boolean;
  generate: (promptId: string) => Promise<void>;
  deleteFormat: (formatId: string) => Promise<void>;
  selectFormat: (format: NoteFormatResponse | null) => void;
  refreshFormats: () => Promise<void>;
}

export const useNoteFormats = (noteId: string): UseNoteFormatsReturn => {
  const [formatTypes, setFormatTypes] = useState<FormatType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [generatedFormats, setGeneratedFormats] = useState<NoteFormatResponse[]>([]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const { showAlert } = useAlertStore();
  const cancelledRef = useRef(false);
  const generatingRef = useRef(false);

  const activeResult = useNoteFormatDisplayStore((s) => s.activeResult);
  const setActiveFormat = useNoteFormatDisplayStore((s) => s.setActiveFormat);
  const clearActiveFormat = useNoteFormatDisplayStore((s) => s.clearActiveFormat);
  const setRegenerateHandler = useNoteFormatDisplayStore((s) => s.setRegenerateHandler);
  const setDeleteHandler = useNoteFormatDisplayStore((s) => s.setDeleteHandler);
  const setIsRegenerating = useNoteFormatDisplayStore((s) => s.setIsRegenerating);
  const setStoreGeneratedFormats = useNoteFormatDisplayStore((s) => s.setGeneratedFormats);
  const setStoreDisplayNameMap = useNoteFormatDisplayStore((s) => s.setDisplayNameMap);
  const setActiveTab = useNoteFormatDisplayStore((s) => s.setActiveTab);

  // Build display name lookup from format types
  const displayNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of formatTypes) {
      map[t.prompt_value] = t.display_name;
    }
    return map;
  }, [formatTypes]);

  // Sync displayNameMap to store for NoteContentTabs
  useEffect(() => {
    setStoreDisplayNameMap(displayNameMap);
  }, [displayNameMap, setStoreDisplayNameMap]);

  const getDisplayName = useCallback(
    (format: NoteFormatResponse) =>
      displayNameMap[format.prompt_value] ||
      format.prompt_value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    [displayNameMap],
  );

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      clearActiveFormat();
      setRegenerateHandler(null);
      setDeleteHandler(null);
      setStoreGeneratedFormats([]);
      setStoreDisplayNameMap({});
      setActiveTab('original');
    };
  }, [
    clearActiveFormat,
    setRegenerateHandler,
    setDeleteHandler,
    setStoreGeneratedFormats,
    setStoreDisplayNameMap,
    setActiveTab,
  ]);

  // Fetch available format types on mount
  useEffect(() => {
    let cancelled = false;

    const loadTypes = async () => {
      try {
        const types = await fetchFormatTypes();
        if (!cancelled) setFormatTypes(types);
      } catch (error) {
        if (!cancelled) {
          errorReporter.expected('Failed to load format types', error, {
            feature: 'noteFormats',
            operation: 'loadTypes',
          });
        }
      } finally {
        if (!cancelled) setIsLoadingTypes(false);
      }
    };

    loadTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch existing formats for this note on mount
  const refreshFormats = useCallback(async () => {
    if (!noteId) return;
    setIsLoadingFormats(true);
    try {
      const formats = await fetchNoteFormats(noteId);
      if (!cancelledRef.current) {
        setGeneratedFormats(formats);
        setStoreGeneratedFormats(formats);
      }
    } catch (error) {
      if (!cancelledRef.current) {
        errorReporter.expected('Failed to load note formats', error, {
          feature: 'noteFormats',
          operation: 'loadFormats',
          noteId,
        });
      }
    } finally {
      if (!cancelledRef.current) setIsLoadingFormats(false);
    }
  }, [noteId, setStoreGeneratedFormats]);

  useEffect(() => {
    refreshFormats();
  }, [refreshFormats]);

  const generate = useCallback(
    async (promptId: string) => {
      if (generatingRef.current) return;
      generatingRef.current = true;
      setIsGenerating(true);
      setIsRegenerating(true);

      try {
        const response = await generateNoteFormat({ noteId, promptId });
        if (!cancelledRef.current) {
          const name = getDisplayName(response);
          setActiveFormat(response, name);
          setActiveTab(response.id);
          await refreshFormats();
        }
      } catch (error) {
        if (cancelledRef.current) return;

        const message = error instanceof Error ? error.message : '';

        if (message === 'BACKEND_NOT_AVAILABLE') {
          showAlert('This feature is not available yet. Please try again later.', 'warning');
        } else {
          showAlert('Failed to generate format. Please try again.', 'error');
        }
      } finally {
        generatingRef.current = false;
        if (!cancelledRef.current) {
          setIsGenerating(false);
          setIsRegenerating(false);
        }
      }
    },
    [noteId, showAlert, refreshFormats, setActiveFormat, setIsRegenerating, getDisplayName, setActiveTab],
  );

  const deleteFormat = useCallback(
    async (formatId: string) => {
      try {
        await deleteNoteFormat(noteId, formatId);
        if (!cancelledRef.current) {
          // If we just deleted the actively viewed format, close the viewer
          if (activeResult?.id === formatId) {
            clearActiveFormat();
          }
          await refreshFormats();
          showAlert('Format deleted.', 'success');
        }
      } catch (error) {
        if (!cancelledRef.current) {
          errorReporter.expected('Failed to delete note format', error, {
            feature: 'noteFormats',
            operation: 'deleteFormat',
            noteId,
          });
          showAlert('Failed to delete format. Please try again.', 'error');
        }
      }
    },
    [noteId, activeResult?.id, clearActiveFormat, refreshFormats, showAlert],
  );

  // Register regenerate and delete handlers whenever generate or activeResult changes
  useEffect(() => {
    if (activeResult) {
      setRegenerateHandler(() => {
        generate(activeResult.prompt_id);
      });
      setDeleteHandler(() => {
        deleteFormat(activeResult.id);
      });
    }
    return () => {
      setRegenerateHandler(null);
      setDeleteHandler(null);
    };
  }, [activeResult, generate, deleteFormat, setRegenerateHandler, setDeleteHandler]);

  const selectFormat = useCallback(
    (format: NoteFormatResponse | null) => {
      if (format) {
        setActiveFormat(format, getDisplayName(format));
      } else {
        clearActiveFormat();
      }
    },
    [setActiveFormat, clearActiveFormat, getDisplayName],
  );

  return {
    formatTypes,
    isLoadingTypes,
    generatedFormats,
    isLoadingFormats,
    activeResult,
    isGenerating,
    generate,
    deleteFormat,
    selectFormat,
    refreshFormats,
  };
};
